import React from "react";
import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { DayWorkStatusKind } from "../types/dashboard2-work-status.types";
import {
  dashboard2AdditionalBookingBadgeSx,
  dashboard2AdditionalBookingBadgesRowSx
} from "../dashboard2-status.styles";
import {
  filterRedundantParticipantTags,
  participantTagTranslationKey
} from "../utils/dashboard2-booking-participant-tags.utils";

type BookingBadge = {
  id: string;
  typeLabel?: string;
  participantTags?: readonly ("visitor" | "external")[];
};

type Dashboard2BookingBadgeChipsProps = {
  badges: BookingBadge[];
  statusKind: DayWorkStatusKind;
  testId?: string;
};

export const Dashboard2BookingBadgeChips: React.FC<Dashboard2BookingBadgeChipsProps> = ({
  badges,
  statusKind,
  testId
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (badges.length === 0) {
    return null;
  }

  const shownParticipantTags = new Set<"visitor" | "external">();

  return (
    <Box sx={dashboard2AdditionalBookingBadgesRowSx} data-testid={testId}>
      {badges.map(badge => {
        const participantTags = filterRedundantParticipantTags(
          badge.typeLabel,
          badge.participantTags ?? []
        ).filter(tag => {
          if (shownParticipantTags.has(tag)) {
            return false;
          }

          shownParticipantTags.add(tag);
          return true;
        });

        const chips = [
          ...(badge.typeLabel ? [{ key: `${badge.id}-type`, label: badge.typeLabel }] : []),
          ...participantTags.map(tag => ({
            key: `${badge.id}-${tag}`,
            label: t(participantTagTranslationKey(tag))
          }))
        ];

        if (chips.length === 0) {
          return null;
        }

        return (
          <Box
            key={badge.id}
            sx={{ display: "inline-flex", flexWrap: "wrap", gap: 0.5, maxWidth: "100%" }}
            data-testid={`dashboard2-booking-badge-group-${badge.id}`}
          >
            {chips.map(chip => (
              <Box
                key={chip.key}
                component="span"
                sx={dashboard2AdditionalBookingBadgeSx(theme, statusKind)}
                data-testid={`dashboard2-booking-badge-${chip.key}`}
              >
                {chip.label}
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
};
